/**
 * AuthDrawer — unified phone → OTP → profile drawer for wa_otp / wa_otp_custom modes.
 *
 * Step 1: Phone number entry
 * Step 2: OTP verification (6-digit code, 5-min countdown, resend)
 * Step 3a: Already registered → "Ciao Nome, vuoi procedere?" confirm
 * Step 3b: New customer → fill type / name / surname (+ address if delivery)
 *
 * Props:
 *   open            {boolean}
 *   onClose         {function}
 *   primaryColor    {string}
 *   tenantId        {string}
 *   requireAddress  {boolean} — true when delivery is chosen in cart
 *   onAuthenticated {function(customer, accessToken)} — called after successful auth/registration
 */

import { useEffect, useRef, useState } from "react";
import { AlertCircle, ArrowLeft, LoaderCircle, X } from "lucide-react";
import api from "../lib/api.js";
import { savePersistentToken, removeSessionToken } from "../lib/tokenStorage.js";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "./ui/input-otp.jsx";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer.jsx";

const COUNTRY_PREFIXES = [
  { code: "+39", flag: "\ud83c\uddee\ud83c\uddf9", label: "IT" },
  { code: "+44", flag: "\ud83c\uddec\ud83c\udde7", label: "UK" },
  { code: "+33", flag: "\ud83c\uddeb\ud83c\uddf7", label: "FR" },
  { code: "+49", flag: "\ud83c\udde9\ud83c\uddea", label: "DE" },
  { code: "+34", flag: "\ud83c\uddea\ud83c\uddf8", label: "ES" },
  { code: "+41", flag: "\ud83c\udde8\ud83c\udded", label: "CH" },
  { code: "+31", flag: "\ud83c\uddf3\ud83c\uddf1", label: "NL" },
  { code: "+32", flag: "\ud83c\udde7\ud83c\uddea", label: "BE" },
  { code: "+1",  flag: "\ud83c\uddfa\ud83c\uddf8", label: "US" },
];

const OTP_SECONDS = 300; // 5 minutes

function formatCountdown(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function StepPhone({ primaryColor, onNext, tenantId }) {
  const [prefix, setPrefix] = useState("+39");
  const [number, setNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const phone = `${prefix}${number.trim()}`;
      const { data } = await api.post(
        "/webapp/auth/otp/send",
        { phone },
        { headers: { "x-tenant-id": tenantId } }
      );
      onNext({ phone, otpToken: data.data.otpToken });
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Errore nell'invio del codice. Riprova.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1">
        <label className="text-xs font-semibold text-[#475569]">Numero WhatsApp</label>
        <div className="flex gap-2">
          {/* Country prefix selector */}
          <select
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            className="h-[42px] w-24 shrink-0 rounded-xl border border-[#e2e8f0] bg-[#f1f5f9] px-2 text-sm font-semibold text-[#0f172a] focus:outline-none"
          >
            {COUNTRY_PREFIXES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
          <input
            type="tel"
            inputMode="numeric"
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, "").slice(0, 12))}
            placeholder="3XX XXX XXXX"
            autoFocus
            required
            className="min-w-0 flex-1 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#94a3b8] focus:outline-none"
          />
        </div>
        <p className="text-[11px] text-[#94a3b8]">
          Ti invieremo un codice di verifica su WhatsApp.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-sm text-[#ef4444]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || number.trim().length < 6}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold uppercase tracking-wide text-white disabled:opacity-50"
        style={{ backgroundColor: primaryColor }}
      >
        {submitting ? (
          <>
            <LoaderCircle className="h-4 w-4 animate-spin" />
            <span>Invio codice...</span>
          </>
        ) : (
          "Invia codice"
        )}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — OTP
// ---------------------------------------------------------------------------
function StepOtp({ phone, otpToken: initialOtpToken, primaryColor, tenantId, onNext, onBack }) {
  const [code, setCode] = useState("");
  const [otpToken, setOtpToken] = useState(initialOtpToken);
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(OTP_SECONDS);

  // countdown
  const intervalRef = useRef(null);
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [otpToken]);

  async function handleVerify(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post(
        "/webapp/auth/otp/verify",
        { otpToken, code: code.trim() },
        { headers: { "x-tenant-id": tenantId } }
      );
      onNext(data.data); // { accessToken, isNewCustomer, guest, customer }
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Codice non valido. Riprova.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    setResending(true);
    setError("");
    try {
      const { data } = await api.post(
        "/webapp/auth/otp/send",
        { phone },
        { headers: { "x-tenant-id": tenantId } }
      );
      setOtpToken(data.data.otpToken);
      setCode("");
      setSecondsLeft(OTP_SECONDS);
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Errore nel rinvio del codice.");
    } finally {
      setResending(false);
    }
  }

  return (
    <form onSubmit={handleVerify} className="space-y-5">
      <p className="text-sm text-[#475569]">
        Codice inviato a <span className="font-semibold text-[#0f172a]">{phone}</span>.{" "}
        Inserisci le 6 cifre ricevute su WhatsApp.
      </p>

      {/* Shadcn InputOTP — 6 slot */}
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(val) => setCode(val)}
          autoFocus
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      {/* Countdown / resend */}
      <div className="text-center text-xs text-[#94a3b8]">
        {secondsLeft > 0 ? (
          <span>Il codice scade tra <span className="font-semibold text-[#475569]">{formatCountdown(secondsLeft)}</span></span>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="font-semibold text-[#475569] underline disabled:opacity-50"
          >
            {resending ? "Invio in corso..." : "Invia un nuovo codice"}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-sm text-[#ef4444]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || code.length !== 6}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold uppercase tracking-wide text-white disabled:opacity-50"
        style={{ backgroundColor: primaryColor }}
      >
        {submitting ? (
          <>
            <LoaderCircle className="h-4 w-4 animate-spin" />
            <span>Verifica...</span>
          </>
        ) : (
          "Verifica codice"
        )}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center justify-center gap-1 text-sm text-[#64748b]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Cambia numero
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step 3a — Already registered: confirm
// ---------------------------------------------------------------------------
function StepConfirm({ customer, primaryColor, context, onConfirm }) {
  const firstName = customer.name || "";
  const greeting = firstName ? `Ciao, ${firstName}!` : "Bentornato!";

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-center gap-2 py-2 text-center">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full text-2xl font-bold text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {firstName ? firstName[0].toUpperCase() : "👤"}
        </div>
        <p className="text-lg font-bold text-[#0f172a]">{greeting} 👋</p>
        <p className="text-sm text-[#64748b]">
          {context === "checkout"
            ? "Il tuo numero è verificato. Pronto per completare l'ordine?"
            : "Accesso effettuato. Puoi ora consultare i tuoi ordini e il profilo."}
        </p>
      </div>

      <button
        type="button"
        onClick={onConfirm}
        className="flex h-14 w-full items-center justify-center rounded-xl text-sm font-bold uppercase tracking-wide text-white"
        style={{ backgroundColor: primaryColor }}
      >
        {context === "checkout" ? "Conferma e procedi" : "Entra nel tuo account"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3b — New customer: fill profile
// ---------------------------------------------------------------------------
function StepProfile({ customer, primaryColor, tenantId, requireAddress, context, onComplete }) {
  const [type, setType] = useState("b2c");
  const [form, setForm] = useState({
    name: customer.name || "",
    surname: customer.surname || "",
    email: "",
    companyName: "",
    vatNumber: "",
    address: { street: "", city: "", province: "", zip: "", country: "IT" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isB2B = type === "b2b";
  const needsAddress = requireAddress || isB2B;

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        type,
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: form.email.trim() || undefined,
        phone: customer.phone,
        ...(isB2B
          ? { companyName: form.companyName.trim(), vatNumber: form.vatNumber.trim(), address: form.address }
          : needsAddress
            ? { address: form.address }
            : {}),
      };
      const { data } = await api.post("/webapp/customers/register", payload, {
        headers: { "x-tenant-id": tenantId },
      });
      const result = data?.data || {};
      if (result.accessToken) {
        savePersistentToken(tenantId, result.accessToken);
      }
      onComplete(result.customer || {}, result.accessToken);
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Errore nella registrazione. Riprova.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#94a3b8] focus:outline-none";
  const labelClass = "text-xs font-semibold text-[#475569]";

  return (
    <form id="auth-profile-form" onSubmit={handleSubmit} className="space-y-3">
      {/* Privato / Azienda toggle */}
      <div className="flex rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-1">
        {["b2c", "b2b"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setType(t)}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition-colors ${
              type === t ? "bg-white text-[#0f172a] shadow-sm" : "text-[#64748b]"
            }`}
          >
            {t === "b2c" ? "Privato" : "Azienda"}
          </button>
        ))}
      </div>

      {/* Nome + Cognome */}
      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <label className={labelClass}>Nome *</label>
          <input type="text" value={form.name} onChange={(e) => setField("name", e.target.value)}
            placeholder="Mario" className={inputClass} required />
        </div>
        <div className="flex-1 space-y-1">
          <label className={labelClass}>Cognome *</label>
          <input type="text" value={form.surname} onChange={(e) => setField("surname", e.target.value)}
            placeholder="Rossi" className={inputClass} required />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-1">
        <label className={labelClass}>Email {isB2B ? "*" : ""}</label>
        <input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)}
          placeholder="mario@example.com" className={inputClass} required={isB2B} />
      </div>

      {/* B2B extra */}
      {isB2B && (
        <>
          <div className="space-y-1">
            <label className={labelClass}>Ragione Sociale *</label>
            <input type="text" value={form.companyName} onChange={(e) => setField("companyName", e.target.value)}
              placeholder="Azienda Srl" className={inputClass} required />
          </div>
          <div className="space-y-1">
            <label className={labelClass}>Partita IVA *</label>
            <input type="text" value={form.vatNumber} onChange={(e) => setField("vatNumber", e.target.value)}
              placeholder="IT12345678901" className={inputClass} required />
          </div>
        </>
      )}

      {/* Address — shown for B2B always, for B2C only if delivery was chosen */}
      {needsAddress && (
        <>
          <div className="space-y-1">
            <label className={labelClass}>Indirizzo *</label>
            <input type="text" value={form.address.street}
              onChange={(e) => setField("address", { ...form.address, street: e.target.value })}
              placeholder="Via Roma 1" className={inputClass} required />
          </div>
          <div className="flex gap-2">
            <div className="flex-1 space-y-1">
              <label className={labelClass}>Città *</label>
              <input type="text" value={form.address.city}
                onChange={(e) => setField("address", { ...form.address, city: e.target.value })}
                placeholder="Milano" className={inputClass} required />
            </div>
            <div className="w-24 space-y-1">
              <label className={labelClass}>CAP *</label>
              <input type="text" value={form.address.zip}
                onChange={(e) => setField("address", { ...form.address, zip: e.target.value })}
                placeholder="20100" className={inputClass} required />
            </div>
          </div>
        </>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-[#fecaca] bg-[#fef2f2] px-3 py-2.5 text-sm text-[#ef4444]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        form="auth-profile-form"
        disabled={submitting}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
        style={{ backgroundColor: primaryColor }}
      >
        {submitting ? (
          <>
            <LoaderCircle className="h-4 w-4 animate-spin" />
            <span>Salvataggio...</span>
          </>
        ) : context === "checkout" ? (
          "Crea account e procedi"
        ) : (
          "Crea account"
        )}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Main drawer
// ---------------------------------------------------------------------------
const STEP_TITLES = {
  phone: "Accedi",
  otp: "Verifica il numero",
  confirm: "Bentornato",
  profile: "Completa il profilo",
};
const STEP_DESCRIPTIONS = {
  phone: "Inserisci il tuo numero WhatsApp per continuare.",
  otp: "Controlla WhatsApp e inserisci il codice ricevuto.",
  confirm: "Abbiamo ritrovato il tuo account.",
  profile: "Inserisci i tuoi dati per completare la registrazione.",
};

export default function AuthDrawer({
  open,
  onClose,
  primaryColor = "#38bdf8",
  tenantId,
  requireAddress = false,
  context = "standalone", // "checkout" | "standalone"
  onAuthenticated,
}) {
  const [step, setStep] = useState("phone");
  const [phoneState, setPhoneState] = useState({ phone: "", otpToken: "" });
  const [verifiedData, setVerifiedData] = useState(null); // from /otp/verify

  // Reset to step 1 when drawer opens
  useEffect(() => {
    if (open) {
      // Dev-only: ?debugAuthStep=profile|confirm|otp salta gli step OTP (che costano)
      const debugStep = import.meta.env.DEV
        ? new URLSearchParams(window.location.search).get("debugAuthStep")
        : null;
      if (debugStep && ["profile", "confirm", "otp"].includes(debugStep)) {
        setPhoneState({ phone: "+393330000000", otpToken: "debug" });
        setVerifiedData({
          accessToken: "debug-token",
          isNewCustomer: debugStep === "profile",
          customer: { name: "", surname: "", phone: "+393330000000" },
        });
        setStep(debugStep);
        return;
      }
      setStep("phone");
      setPhoneState({ phone: "", otpToken: "" });
      setVerifiedData(null);
    }
  }, [open]);

  function handlePhoneNext({ phone, otpToken }) {
    setPhoneState({ phone, otpToken });
    setStep("otp");
  }

  function handleOtpNext(data) {
    // data = { accessToken, isNewCustomer, guest, customer }
    savePersistentToken(tenantId, data.accessToken);
    removeSessionToken(); // rimuove token magic-link stale che ha priorità in resolveToken
    setVerifiedData(data);
    setStep(data.isNewCustomer ? "profile" : "confirm");
  }

  function handleConfirm() {
    if (verifiedData) {
      onAuthenticated?.(verifiedData.customer, verifiedData.accessToken);
      onClose();
    }
  }

  function handleProfileComplete(customer, accessToken) {
    onAuthenticated?.(customer, accessToken);
    onClose();
  }

  const title = STEP_TITLES[step] || "";
  const description = STEP_DESCRIPTIONS[step] || "";
  const canGoBack = step === "otp";

  return (
    <Drawer
      open={open}
      dismissible={step !== "otp"}
      onOpenChange={(v) => { if (!v) onClose(); }}
    >
      {/* maxHeight in dvh (viewport dinamico) sovrascrive il fallback vh dove supportato */}
      <DrawerContent
        className="mx-auto w-full max-w-[800px] rounded-t-2xl border-[#e2e8f0] bg-white max-h-[90vh]"
        style={{ maxHeight: "90dvh" }}
      >
        <DrawerHeader className="relative shrink-0 pb-2 text-left">
          {canGoBack && (
            <button
              type="button"
              onClick={() => setStep("phone")}
              className="absolute left-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b]"
              aria-label="Indietro"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <DrawerTitle className={`text-xl font-semibold text-[#0f172a] ${canGoBack ? "pl-10" : ""}`}>
            {title}
          </DrawerTitle>
          <DrawerDescription className="text-sm text-[#64748b]">{description}</DrawerDescription>
          <DrawerClose asChild>
            <button
              type="button"
              className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#f1f5f9] text-[#64748b]"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>
          </DrawerClose>
        </DrawerHeader>

        {/* Nessuna altezza fissa qui: il drawer è alto quanto il contenuto (h-auto).
            min-h-0 permette a quest'area di comprimersi quando il drawer tocca il suo
            max-height (o quando Vaul lo riduce per la tastiera), attivando lo scroll
            interno. overscroll-contain evita il rubber-band sulla pagina dietro. */}
        <div className="min-h-0 overflow-y-auto overscroll-contain px-4 pb-8">
          {step === "phone" && (
            <StepPhone primaryColor={primaryColor} tenantId={tenantId} onNext={handlePhoneNext} />
          )}
          {step === "otp" && (
            <StepOtp
              phone={phoneState.phone}
              otpToken={phoneState.otpToken}
              primaryColor={primaryColor}
              tenantId={tenantId}
              onNext={handleOtpNext}
              onBack={() => setStep("phone")}
            />
          )}
          {step === "confirm" && verifiedData && (
            <StepConfirm
              customer={verifiedData.customer}
              primaryColor={primaryColor}
              context={context}
              onConfirm={handleConfirm}
            />
          )}
          {step === "profile" && verifiedData && (
            <StepProfile
              customer={verifiedData.customer}
              primaryColor={primaryColor}
              tenantId={tenantId}
              requireAddress={requireAddress}
              context={context}
              onComplete={handleProfileComplete}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
